import { Fragment } from "react";
import {
  History,
  Place,
  PrivacyTip,
  Wallet,
  Warning,
} from "@mui/icons-material";
import { formatAmount } from "../../../format";
import { i18n } from "../../../i18n";
import { FormLoader } from "../../commons/Form/Form";
import { Trans, useTranslation } from "react-i18next";
import { useCountries } from "../../../hooks/commons/useCountries";
import { subMonths } from "date-fns";
import {
  Indicators,
  Indicator,
  IndicatorContainer,
  IndicatorIconWrapper,
  IndicatorText,
} from "../../commons/Indicators/Indicators";
import { useIndicators } from "./IndicatorsContext";
import { useAlerts } from "./AlertsContext";
import { useAuth } from "../../../auth/AuthContext";

export default function TreasuryIndicators() {
  const { loading: loadingIndicators } = useIndicators();

  return (
    <Indicators>
      {loadingIndicators ? (
        <FormLoader />
      ) : (
        <Fragment>
          <AgeIndicator />
          <HQIndicator />
          <TreasuryIndicator />
          <AlertsIndicators />
          <ProceedingsIndicator />
        </Fragment>
      )}
    </Indicators>
  );
}

function TreasuryIndicator() {
  const { t } = useTranslation(["common", "treasury"]);
  const { indicators } = useIndicators();
  const { activeCompany, loadingActiveCompany } = useAuth();

  const i18nKey = "treasury:indicators.treasury";

  const color = (value) =>
    value < 0 ? "error" : value < 10000 ? "warning" : "";

  return (
    <Indicator>
      {loadingActiveCompany ? (
        <FormLoader />
      ) : (
        <Fragment>
          <IndicatorIconWrapper className={color(indicators.treasury)}>
            <Wallet />
          </IndicatorIconWrapper>
          <IndicatorContainer>
            <IndicatorText>{t(`${i18nKey}.label`)}</IndicatorText>
            <IndicatorText bold>
              {formatAmount(
                indicators.treasury,
                i18n.resolvedLanguage,
                activeCompany.currency,
              )}
            </IndicatorText>
          </IndicatorContainer>
        </Fragment>
      )}
    </Indicator>
  );
}

function AlertsIndicators() {
  const { t } = useTranslation(["common", "treasury"]);
  const { alerts, loading: loadingAlerts } = useAlerts();

  const i18nKey = "treasury:indicators.alerts";

  const color = (nbAlerts) => (nbAlerts ? "warning" : "");

  const today = () => new Date().setHours(0, 0, 0, 0);
  const sixMonthsAgo = subMonths(today(), 6);
  const filterFn = (alert) =>
    new Date(alert.date).setHours(0, 0, 0, 0) > sixMonthsAgo;

  return (
    <Indicator>
      {loadingAlerts ? (
        <FormLoader />
      ) : (
        <Fragment>
          <IndicatorIconWrapper className={color(alerts.length)}>
            <Warning />
          </IndicatorIconWrapper>
          <IndicatorContainer>
            <IndicatorText>{t(`${i18nKey}.label`)}</IndicatorText>
            <IndicatorText bold>{alerts.filter(filterFn).length}</IndicatorText>
            <IndicatorText small>{t(`${i18nKey}.note`)}</IndicatorText>
          </IndicatorContainer>
        </Fragment>
      )}
    </Indicator>
  );
}

function ProceedingsIndicator() {
  const { t } = useTranslation(["common", "treasury"]);
  const { indicators } = useIndicators();

  const i18nKey = "treasury:indicators.proceedings";

  const color = (value) => (value ? "error" : "");

  return (
    <Indicator>
      <IndicatorIconWrapper className={color(indicators.proceedings_count)}>
        <PrivacyTip />
      </IndicatorIconWrapper>
      <IndicatorContainer>
        <IndicatorText>{t(`${i18nKey}.label`)}</IndicatorText>
        <IndicatorText bold>{indicators.proceedings_count}</IndicatorText>
        <IndicatorText small>{t(`${i18nKey}.note`)}</IndicatorText>
      </IndicatorContainer>
    </Indicator>
  );
}

function AgeIndicator() {
  const { t } = useTranslation(["common", "treasury"]);
  const { indicators } = useIndicators();

  const i18nKey = "treasury:indicators.age";

  const color = {
    less_than_3_months: "error",
    less_than_6_months: "warning",
    less_than_1_year: "warning",
    less_than_3_years: "",
    less_than_5_years: "",
    more_than_5_years: "success",
  };

  return (
    <Indicator>
      <IndicatorIconWrapper className={color[indicators.age]}>
        <History />
      </IndicatorIconWrapper>
      <IndicatorContainer>
        <IndicatorText>{t(`${i18nKey}.label`)}</IndicatorText>
        <IndicatorText bold>
          {t(`${i18nKey}.value.${indicators.age}`)}
        </IndicatorText>
      </IndicatorContainer>
    </Indicator>
  );
}

function HQIndicator() {
  const { t } = useTranslation(["common", "treasury"]);
  const { indicators } = useIndicators();
  const { activeCompany, loadingActiveCompany } = useAuth();

  const i18nKey = "treasury:indicators.hq";

  const color = (value) => (value ? "success" : "");

  const countries = useCountries();
  const countriesOptions = Object.entries(
    countries.getNames(i18n.resolvedLanguage, { select: "official" }),
  ).map(([alpha2Code, name]) => {
    return {
      name,
      alpha2Code,
    };
  });

  const countryName = countriesOptions.find(
    (option) => option.alpha2Code === activeCompany.address.country,
  ).name;

  const mainLandValue = (
    <Trans
      t={t}
      i18nKey={`${i18nKey}.value`}
      values={{ countryName: countryName }}
    />
  );

  const value = indicators.in_mainland ? mainLandValue : countryName;

  return (
    <Indicator>
      {loadingActiveCompany ? (
        <FormLoader />
      ) : (
        <Fragment>
          <IndicatorIconWrapper className={color(indicators.in_mainland)}>
            <Place />
          </IndicatorIconWrapper>
          <IndicatorContainer>
            <IndicatorText>{t(`${i18nKey}.label`)}</IndicatorText>
            <IndicatorText bold>{value}</IndicatorText>
          </IndicatorContainer>
        </Fragment>
      )}
    </Indicator>
  );
}
